// src/utils/goldCalculation.js
// ──────────────────────────────────────────────────────────
// Shared gold-item calculation logic for the frontend.
// Used by both Bill (GoldItemsTable) and PledgeReleaseCase.
// ──────────────────────────────────────────────────────────

/**
 * Round amount UP to nearest rupee (0 decimals).
 * Uses Math.ceil to always round UP to avoid short-changing.
 * @param {number} value
 * @returns {number}
 */
export const roundAmount = (value) => {
  return Math.ceil(parseFloat(value || 0));
};

/**
 * Parse numeric values safely, including comma-formatted strings (e.g. "12,179.00").
 * @param {number|string|null|undefined} value
 * @returns {number}
 */
const toNumber = (value) => {
  if (value === null || value === undefined || value === '') return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;

  const cleaned = String(value).replace(/,/g, '').trim();
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Round weight to 2 decimal places.
 * Amount round-off remains separate via roundAmount.
 * @param {number} value
 * @returns {number}
 */
export const roundWeight = (value) => {
  return Number(toNumber(value).toFixed(2));
};

/**
 * Calculate derived fields for a single gold item.
 *
 * Formula:
 *  1. Dust Deduction = (grossWeight - stoneWaxDeduction) / 100
 *  2. beforeNetWt    = grossWeight - stoneWaxDeduction - dustDeduction
 *  3. Net Weight     = beforeNetWt * (purity / 100)
 *  4. Total Price    = netWeight * ratePerGram
 *
 * If dustDeductionOverride is true, use the provided dustDeduction value instead of calculating it.
 *
 * @param {Object} item
 * @param {boolean} options.skipDustRecalc - If true, don't recalculate dustDeduction (user override)
 * @returns {{ dustDeduction: number, netWeight: number, totalPrice: number }}
 */
export const calculateGoldItem = (item = {}, options = {}) => {
  const { skipDustRecalc = false } = options;
  const grossWeight = toNumber(item.grossWeight);
  // Support both old Bill field name (deduction) and new canonical name (stoneWaxDeduction)
  const stoneWaxDeduction = toNumber(item.stoneWaxDeduction ?? item.deduction ?? 0);
  const purity = toNumber(item.purity);
  const rate = toNumber(item.ratePerGram || item.estimatedRatePerGram || 0);

  // Calculate or use override dust deduction
  let dustDeduction = toNumber(item.dustDeduction);
  if (!skipDustRecalc) {
    dustDeduction = roundWeight((grossWeight - stoneWaxDeduction) / 100);
  } else {
    dustDeduction = roundWeight(dustDeduction);
  }

  const beforeNetWt = grossWeight - stoneWaxDeduction - dustDeduction;
  const netWeight = roundWeight(beforeNetWt * (purity / 100));
  const totalPrice = roundAmount(netWeight * rate);

  return { dustDeduction, netWeight, totalPrice };
};

/**
 * Recalculate a single gold item object in-place style (returns new object with calculated fields merged).
 * Works with the legacy Bill field names (deduction) and new PledgeReleaseCase names (stoneWaxDeduction).
 * 
 * @param {Object} item - The item to recalculate
 * @param {Object} options - Options for calculation
 * @param {boolean} options.skipDustRecalc - If true, don't recalculate dustDeduction (use provided value)
 */
export const recalcGoldItem = (item, options = {}) => {
  const { dustDeduction, netWeight, totalPrice } = calculateGoldItem(item, options);
  return { ...item, dustDeduction, netWeight, totalPrice };
};

/**
 * Calculate aggregated totals from an array of gold items.
 *
 * @param {Array} items
 * @returns {{ totalGrossWeight, totalNetWeight, totalStoneDeduction, totalDustDeduction, estimatedTotalAmount }}
 */
export const calculateGoldTotals = (items = []) => {
  let totalGrossWeight = 0;
  let totalNetWeight = 0;
  let totalStoneDeduction = 0;
  let totalDustDeduction = 0;
  let estimatedTotalAmount = 0;

  items.forEach((item) => {
    totalGrossWeight += parseFloat(item.grossWeight || 0);
    totalNetWeight += parseFloat(item.netWeight || 0);
    totalStoneDeduction += parseFloat(item.stoneWaxDeduction ?? item.deduction ?? 0);
    totalDustDeduction += parseFloat(item.dustDeduction || 0);
    estimatedTotalAmount += parseFloat(item.totalPrice || 0);
  });

  return {
    totalGrossWeight: roundWeight(totalGrossWeight),
    totalNetWeight: roundWeight(totalNetWeight),
    totalStoneDeduction: roundWeight(totalStoneDeduction),
    totalDustDeduction: roundWeight(totalDustDeduction),
    estimatedTotalAmount: roundAmount(estimatedTotalAmount),
  };
};

// ──────────────────────────────────────────────────────────
// Case precision (Walk-In / Pledge Release / Gold Receipt)
//
// Weights are kept to 4 decimals, item Total Price and the gold value are
// NOT rounded to a rupee (only trimmed to 4 decimals of float noise), the
// processing fee stays exact, and only the final payable amount is rounded —
// to the NEAREST rupee. The standalone Bill page keeps the functions above.
// ──────────────────────────────────────────────────────────

const PRECISE_FACTOR = 10000;

/** Trim to 4 decimals without rounding to a rupee (weights, prices, fees). */
export const roundPrecise = (value) =>
  Math.round(toNumber(value) * PRECISE_FACTOR) / PRECISE_FACTOR;

/** Final payable amount → nearest rupee (152300.7801 → 152301, 152300.49 → 152300). */
export const roundFinalAmount = (value) => Math.round(roundPrecise(value));

/** Final payable for a case: gold value − processing fee + manual round-off. */
export const calculateCaseFinalAmount = (goldValue, processingFeeAmount = 0, roundingUpAmount = 0) =>
  roundFinalAmount(toNumber(goldValue) - toNumber(processingFeeAmount) + toNumber(roundingUpAmount));

/** Processing fee amount, exact to 4 decimals. */
export const calculateCaseProcessingFee = (goldValue, feePercent = 0) =>
  roundPrecise((toNumber(goldValue) * toNumber(feePercent)) / 100);

/**
 * Case version of calculateGoldItem: same formula, 4-decimal weights and an
 * unrounded Total Price.
 */
export const calculateCaseGoldItem = (item = {}, options = {}) => {
  const { skipDustRecalc = false } = options;
  const grossWeight = toNumber(item.grossWeight);
  const stoneWaxDeduction = toNumber(item.stoneWaxDeduction ?? item.deduction ?? 0);
  const purity = toNumber(item.purity);
  const rate = toNumber(item.ratePerGram || item.estimatedRatePerGram || 0);

  const dustDeduction = skipDustRecalc
    ? roundPrecise(item.dustDeduction)
    : roundPrecise((grossWeight - stoneWaxDeduction) / 100);

  const beforeNetWt = grossWeight - stoneWaxDeduction - dustDeduction;
  const netWeight = roundPrecise(beforeNetWt * (purity / 100));
  const totalPrice = roundPrecise(netWeight * rate);

  return { dustDeduction, netWeight, totalPrice };
};

export const recalcCaseGoldItem = (item, options = {}) => ({
  ...item,
  ...calculateCaseGoldItem(item, options),
});

/** Case version of calculateGoldTotals — nothing is rounded to a rupee. */
export const calculateCaseGoldTotals = (items = []) => {
  const sum = (pick) => items.reduce((s, item) => s + toNumber(pick(item)), 0);
  return {
    totalGrossWeight: roundPrecise(sum((i) => i.grossWeight)),
    totalNetWeight: roundPrecise(sum((i) => i.netWeight)),
    totalStoneDeduction: roundPrecise(sum((i) => i.stoneWaxDeduction ?? i.deduction ?? 0)),
    totalDustDeduction: roundPrecise(sum((i) => i.dustDeduction)),
    estimatedTotalAmount: roundPrecise(sum((i) => i.totalPrice)),
  };
};

/** Display helpers for case figures. */
export const formatWeight4 = (value) => toNumber(value).toFixed(4);

export const formatAmount4 = (value) =>
  new Intl.NumberFormat('en-IN', { minimumFractionDigits: 4, maximumFractionDigits: 4 }).format(toNumber(value));

/**
 * The automatic round-off folded into the final amount:
 * final − (gold value − processing fee + manual round-off). Pass the stored
 * final when there is one, so the line always reconciles with it.
 */
export const calculateCaseRoundOff = (goldValue, processingFeeAmount = 0, roundingUpAmount = 0, finalAmount) => {
  const exact = toNumber(goldValue) - toNumber(processingFeeAmount) + toNumber(roundingUpAmount);
  const final = finalAmount != null ? toNumber(finalAmount) : roundFinalAmount(exact);
  return roundPrecise(final - exact);
};

/** "+ ₹ 0.2199" / "- ₹ 0.4900" for the round-off line. */
export const formatRoundOff = (value) =>
  `${toNumber(value) < 0 ? '-' : '+'} ₹ ${formatAmount4(Math.abs(toNumber(value)))}`;
